import * as FileSaver from 'file-saver';
import * as Bixby from '$/core';
import dayjs from 'dayjs';

export abstract class ClientAdminService {
  private static baseUrl = `${Bixby.Constants.API_ADMIN_PREFIX}/${Bixby.Constants.API_CLIENTCONTROLLER}`;
  public static async getAll(): Promise<Bixby.Models.ClientSlim[]> {
    const route = Bixby.API.ServerRoute.forAction(ClientAdminService.baseUrl);
    const results: Bixby.Models.ClientSlim[] = await Bixby.API.get(route);

    if (results) {
      results.forEach(result => {
        result.firstAccess = Bixby.Utils.parseUtcDateFromApi(result.firstAccess as any);
        result.lastAccess = Bixby.Utils.parseUtcDateFromApi(result.lastAccess as any);
        result.daysOnPortal = result.firstAccess ? Math.abs(dayjs(result.firstAccess).diff(dayjs.utc(), 'days')) : null;
      });
    }

    return results;
  }

  public static async create(payload: Bixby.Models.CreateClientRequest): Promise<Bixby.Models.Client> {
    const route = Bixby.API.ServerRoute.forAction(ClientAdminService.baseUrl);
    return await Bixby.API.post(route, payload);
  }

  public static async getById(id: string): Promise<Bixby.Models.Client> {
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}/${id}`);
    return await Bixby.API.get(route);
  }

  public static async importCompanies(clientId: string, companiesFile: File): Promise<Bixby.Models.User> {
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}/${clientId}/${Bixby.Constants.API_ACTION_IMPORT}`);
    const formData = new FormData();
    formData.append('companiesFile', companiesFile);
    return await Bixby.API.post(route, formData);
  }

  public static async getTemplateFile(): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}/${Bixby.Constants.API_ACTION_IMPORT_TEMPLATE}`);
    const blob = await Bixby.API.getFile(route);
    FileSaver.saveAs(blob, "ImportTemplate.xlsx");
  }

  public static async putById(id: string, payload: Bixby.Models.UpdateClientRequest): Promise<Bixby.Models.Client> {
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}/${id}`);
    return await Bixby.API.put(route, payload);
  }

  public static async deleteById(id: string): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}/${id}`);
    return await Bixby.API.delete(route);
  }

  public static async removeFromCompany(clientId: string, companyId: string) {
    const route = Bixby.API.ServerRoute.forAction(
      `${ClientAdminService.baseUrl}/${clientId}/${Bixby.Constants.API_ACTION_COMPANIES}/${companyId}`
    );
    return await Bixby.API.delete(route);
  }

  public static async getUsersByClientId(id: string): Promise<Bixby.Models.UserSlim[]> {
    const action = `${id}/${Bixby.Constants.API_ACTION_USERS}`;
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}`, action);
    return await Bixby.API.get(route);
  }

  public static async verifyUserEmailConfirmationToken(clientId: string, token: string): Promise<void> {
    const action = `${clientId}/${Bixby.Constants.API_ACTION_CLIENT_COMPANYIVITETOKENS}/${encodeURIComponent(token)}/${
      Bixby.Constants.API_ACTION_CLIENT_COMPANYIVITETOKENS_VERIFY
    }`;
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_CLIENTCONTROLLER}`, action);
    await Bixby.API.patch(route, null);
  }

  public static async createApiCredentials(id: string): Promise<Bixby.Models.ClientApiCredentials> {
    const action = `${id}/${Bixby.Constants.API_ACTION_CLIENT_APICREDENTIALS}`;
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}`, action);
    return await Bixby.API.post(route, null);
  }

  public static async regenerateApiCredentials(id: string): Promise<Bixby.Models.ClientApiCredentials> {
    const action = `${id}/${Bixby.Constants.API_ACTION_CLIENT_APICREDENTIALS}`;
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}`, action);
    return await Bixby.API.put(route, null);
  }

  public static async deleteApiCredentials(id: string): Promise<void> {
    const action = `${id}/${Bixby.Constants.API_ACTION_CLIENT_APICREDENTIALS}`;
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}`, action);
    return await Bixby.API.delete(route);
  }

  public static async getClientCompaniesByCompany(
    companyId: string
  ): Promise<Bixby.Models.ClientCompanyDocumentHosting[]> {
    const url = `${ClientAdminService.baseUrl}/client-companies/${companyId}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.get(route);
  }

  public static async validateClientDataSiteProvider(
    clientDataSiteProviderId: string,
    companyId: string
  ): Promise<Bixby.Models.ClientDataSiteProvider> {
    const action = `${Bixby.Constants.API_ACTION_USER_DATASITEPROVIDERS}/${Bixby.Constants.API_ACTION_VALIDATE}`;
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}`, action);
    return await Bixby.API.post(route, { clientDataSiteProviderId, companyId });
  }

  public static async enablePublicCompanies(clientId: string): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}/${clientId}/enable-public-companies`);
    return await Bixby.API.post(route, {});
  }

  public static async disablePublicCompanies(clientId: string): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(`${ClientAdminService.baseUrl}/${clientId}/disable-public-companies`);
    return await Bixby.API.post(route, {});
  }
}
